import React, { createContext, useContext, useReducer, useEffect } from 'react';
import toast from 'react-hot-toast';
import api from '../api';

const AuthContext = createContext();

const initialState = {
  user: null,
  token: localStorage.getItem('token'),
  isAuthenticated: false,
  loading: true,
  error: null
};

const getApiErrorMessage = (error, fallback) => {
  const message = error.response?.data?.message;
  if (message) return message;
  if (error.response?.status === 429) {
    return 'Too many attempts. Please wait a few minutes and try again.';
  }
  return fallback;
};

const authReducer = (state, action) => {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'LOGIN_SUCCESS':
      localStorage.setItem('token', action.payload.token);
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        loading: false,
        error: null
      };
    case 'LOGOUT':
      localStorage.removeItem('token');
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        loading: false,
        error: null
      };
    case 'SET_ERROR':
      return { ...state, error: action.payload, loading: false };
    case 'CLEAR_ERROR':
      return { ...state, error: null };
    case 'UPDATE_USER':
      return { ...state, user: { ...state.user, ...action.payload } };
    default:
      return state;
  }
};

export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Set up axios interceptors
  useEffect(() => {
    // Request interceptor
    const requestInterceptor = api.interceptors.request.use(
      (config) => {
        if (state.token) config.headers.Authorization = `Bearer ${state.token}`;
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor
    const responseInterceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        const isLoginRequest = error.config?.url?.includes('/api/auth/login');
        if (error.response?.status === 401 && !isLoginRequest) {
          dispatch({ type: 'LOGOUT' });
          toast.error('Session expired. Please login again.');
        }
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
    };
  }, [state.token]);

  // Check if user is logged in on app start
  useEffect(() => {
    const checkAuth = async () => {
      if (state.token) {
        try {
          const response = await api.get('/api/auth/me');
          const userFromMe = response.data.user;
          const normalizedUser = { id: userFromMe._id, ...userFromMe };
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: {
              user: normalizedUser,
              token: state.token
            }
          });
        } catch (error) {
          dispatch({ type: 'LOGOUT' });
        }
      } else {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };
    checkAuth();
  }, [state.token]);

  const login = async (identifier, password) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const response = await api.post('/api/auth/login', { identifier, password });
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: response.data.user,
          token: response.data.token
        }
      });

      toast.success('Login successful!');
      return { success: true };
    } catch (error) {
      const message = getApiErrorMessage(error, 'Login failed');
      dispatch({ type: 'SET_ERROR', payload: message });
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await api.post('/api/auth/logout');
    } catch (error) {
      // Ignore logout errors
    } finally {
      dispatch({ type: 'LOGOUT' });
      toast.success('Logged out successfully');
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await api.put('/api/auth/change-password', {
        currentPassword,
        newPassword
      });
      toast.success('Password changed successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to change password';
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const register = async ({ name, email, password, employeeId }) => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'CLEAR_ERROR' });

      const response = await api.post('/api/auth/signup', {
        name,
        email,
        password,
        employeeId
      });

      // Auto-login after register
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: response.data.user,
          token: response.data.token
        }
      });

      toast.success('Account created successfully!');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Registration failed';
      dispatch({ type: 'SET_ERROR', payload: message });
      if (Array.isArray(error.response?.data?.errors)) {
        error.response.data.errors.slice(0, 3).forEach((e) => toast.error(e.msg || e.message));
      } else {
        toast.error(message);
      }
      return { success: false, error: message };
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const userId = state.user?.id || state.user?._id;

      // Whitelist and sanitize payload
      const allowedTop = ['name']; // email updates not allowed via UI
      const allowedProfile = ['phone','dateOfBirth','nid','bloodGroup','address','designation'];

      const clean = {};
      allowedTop.forEach((k) => {
        if (Object.prototype.hasOwnProperty.call(profileData, k)) {
          let v = profileData[k];
          if (typeof v === 'string') v = v.trim();
          if (v !== '') clean[k] = v;
        }
      });

      if (profileData.profile && typeof profileData.profile === 'object') {
        const cp = {};
        allowedProfile.forEach((k) => {
          if (Object.prototype.hasOwnProperty.call(profileData.profile, k)) {
            let v = profileData.profile[k];
            if (typeof v === 'string') v = v.trim();
            if (v === '') return;
            if (k === 'bloodGroup') {
              const allowedBG = ['A+','A-','B+','B-','AB+','AB-','O+','O-'];
              if (!allowedBG.includes(v)) return;
            }
            cp[k] = v;
          }
        });
        if (Object.keys(cp).length) clean.profile = cp;
      }

      const response = await api.put(`/api/users/${userId}`, clean);
      dispatch({ type: 'UPDATE_USER', payload: response.data.user });
      toast.success('Profile updated successfully');
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to update profile';
      const details = error.response?.data?.errors;
      if (Array.isArray(details) && details.length) {
        details.slice(0, 3).forEach((e) => toast.error(`${e.field}: ${e.message}`));
      } else {
        toast.error(message);
      }
      return { success: false, error: message };
    }
  };

  const hasRole = (roles) => {
    if (!state.user) return false;
    if (Array.isArray(roles)) {
      return roles.includes(state.user.role);
    }
    return state.user.role === roles;
  };

  const canAccess = (resource) => {
    if (!state.user) return false;
    
    const { role } = state.user;
    
    switch (resource) {
      case 'users':
        return ['admin', 'director', 'accounts'].includes(role);
      case 'create_user':
        return role === 'admin';
      case 'clients':
        return ['admin', 'director', 'accounts'].includes(role);
      case 'assets':
        return ['admin', 'accounts'].includes(role);
      case 'cars':
        return ['admin', 'accounts'].includes(role);
      case 'office':
        return ['admin', 'director', 'accounts'].includes(role);
      case 'reports':
        return ['admin', 'director', 'accounts'].includes(role);
      case 'payroll':
        return ['admin', 'director', 'accounts', 'employee'].includes(role);
      case 'approve_expenses':
        return ['admin', 'director', 'accounts'].includes(role);
      case 'edit_data':
        return ['admin', 'director'].includes(role);
      default:
        return true;
    }
  };

  const updateUser = (partial) => {
    dispatch({ type: 'UPDATE_USER', payload: partial });
  };

  const value = {
    ...state,
    login,
    logout,
    changePassword,
    register,
    updateProfile,
    updateUser,
    hasRole,
    canAccess
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
